import { RefreshCw, CheckCircle, ExternalLink, ArrowRight, AlertCircle } from 'lucide-react';
import { ProjectData } from '../types';
import { resolveSupportContact, supportPhoneTelHref } from '../utils/supportContact';

interface StageForUpdateProps {
  project: ProjectData;
  updateDetails?: {
    version: string;
    features?: string[];
    improvements?: string[];
    migrationDate?: string;
  };
}

export const StageForUpdate = ({ project, updateDetails }: StageForUpdateProps) => {
  const { appType, jiraKey } = project;
  const appName = appType === 'versirent' ? 'VersiRent' : 'IdealOne';
  const support = resolveSupportContact(appType, project.supportContact);

  return (
    <div className="space-y-6">
      {/* Update Available Banner */}
      <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg p-8 text-white text-center">
        <div className="text-6xl mb-4">🔄</div>
        <h2 className="text-3xl font-bold mb-2">App Update Available!</h2>
        <p className="text-xl opacity-90">
          A new version of your {appName} app is ready for transition
        </p>
      </div>

      {/* Update Information */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <RefreshCw className="h-6 w-6 text-violet-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-violet-800 mb-2">
              🆕 New Version Ready
            </h3>
            <p className="text-violet-700 mb-4">
              Exciting news! A new version of your {appName} app is ready. We're transitioning you to the latest version with improved features and performance.
            </p>
            <div className="bg-white border border-violet-200 rounded-md p-4">
              <p className="text-sm text-violet-800 font-medium mb-2">🚀 Enhanced Experience</p>
              <p className="text-sm text-violet-700">
                This update brings significant improvements to your app experience with enhanced functionality and performance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Update Details */}
      {updateDetails && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            📋 Update Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-violet-100 text-violet-800 rounded-full w-8 h-8 flex items-center justify-center">
                  📱
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Version Information</h4>
                  <p className="text-sm text-gray-600">New version: {updateDetails.version}</p>
                </div>
              </div>
              {updateDetails.migrationDate && (
                <div className="flex items-center space-x-3">
                  <div className="bg-violet-100 text-violet-800 rounded-full w-8 h-8 flex items-center justify-center">
                    📅
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Planned Migration</h4>
                    <p className="text-sm text-gray-600">Date: {updateDetails.migrationDate}</p>
                  </div>
                </div>
              )}
            </div>
            <div>
              {updateDetails.features && updateDetails.features.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">✨ New Features</h4>
                  <ul className="space-y-1">
                    {updateDetails.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <ArrowRight className="h-4 w-4 text-violet-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {updateDetails.improvements && updateDetails.improvements.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">🔧 Improvements</h4>
                  <ul className="space-y-1">
                    {updateDetails.improvements.map((improvement, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Migration Process */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-800 mb-2">
              🔄 Migration Process
            </h3>
            <p className="text-amber-700 mb-4">
              We'll handle the transition to the new version for you. Your app will be updated automatically, and you'll have access to all the new features and improvements.
            </p>
            <div className="bg-white border border-amber-200 rounded-md p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">✅ Seamless Transition</p>
              <p className="text-sm text-amber-700">
                The migration process is designed to be seamless with minimal downtime expected.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Migration Timeline */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          ⏱️ Migration Timeline
        </h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
              Now
            </div>
            <div>
              <p className="font-medium text-gray-900">Update is ready and staged for deployment</p>
              <p className="text-sm text-gray-600">Your new version is prepared and ready to go live</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
              24-48h
            </div>
            <div>
              <p className="font-medium text-gray-900">Migration process begins</p>
              <p className="text-sm text-gray-600">We'll start transitioning your app to the new version</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
              During
            </div>
            <div>
              <p className="font-medium text-gray-900">Minimal downtime expected</p>
              <p className="text-sm text-gray-600">The transition will be as smooth as possible</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
              ✓
            </div>
            <div>
              <p className="font-medium text-gray-900">Update complete</p>
              <p className="text-sm text-gray-600">Your app will be running the latest version</p>
            </div>
          </div>
        </div>
      </div>

      {/* What to Expect */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          📋 What to Expect
        </h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900">Automatic Update</p>
              <p className="text-blue-800 text-sm">Your app will be updated automatically without any action required from you</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900">Immediate Access</p>
              <p className="text-blue-800 text-sm">You'll have immediate access to all new features and improvements</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900">Notification</p>
              <p className="text-blue-800 text-sm">We'll notify you once the update is complete</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900">Enhanced Performance</p>
              <p className="text-blue-800 text-sm">Your app will run faster and more efficiently</p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits of Updating */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          🎯 Benefits of This Update
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                ⚡
              </div>
              <div>
                <p className="font-medium text-gray-900">Improved Performance</p>
                <p className="text-gray-600 text-sm">Faster loading times and smoother operation</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                🛡️
              </div>
              <div>
                <p className="font-medium text-gray-900">Enhanced Security</p>
                <p className="text-gray-600 text-sm">Latest security patches and protections</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                🎨
              </div>
              <div>
                <p className="font-medium text-gray-900">Better User Experience</p>
                <p className="text-gray-600 text-sm">Improved interface and user interactions</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                🔧
              </div>
              <div>
                <p className="font-medium text-gray-900">Bug Fixes</p>
                <p className="text-gray-600 text-sm">Resolved known issues and improvements</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                📱
              </div>
              <div>
                <p className="font-medium text-gray-900">New Features</p>
                <p className="text-gray-600 text-sm">Additional functionality and capabilities</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                🔄
              </div>
              <div>
                <p className="font-medium text-gray-900">Future-Ready</p>
                <p className="text-gray-600 text-sm">Prepared for upcoming enhancements</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support Resources */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          📚 Support Resources
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href={`/project/${jiraKey}/help#app-updates`}
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              📖
            </div>
            <div>
              <p className="font-medium text-gray-900">App Update Guide</p>
              <p className="text-sm text-gray-600">Learn more about the update process and what to expect</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
          <a
            href={`/project/${jiraKey}/help#new-features`}
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              ✨
            </div>
            <div>
              <p className="font-medium text-gray-900">New Features Documentation</p>
              <p className="text-sm text-gray-600">Detailed information about new features and improvements</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
        </div>
      </div>

      {/* Support Contact */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-violet-900 mb-3">
          🆘 Questions About the Update?
        </h3>
        <p className="text-violet-800 mb-4">
          Our support team is here to help throughout the migration process:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-violet-100 text-violet-800 rounded-full w-8 h-8 flex items-center justify-center">
              ✉️
            </div>
            <div>
              <p className="font-medium text-violet-900">Email Support</p>
              <p className="text-sm text-violet-700">
                <a href={`mailto:${support.email}`} className="hover:text-violet-900">
                  {support.email}
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-violet-100 text-violet-800 rounded-full w-8 h-8 flex items-center justify-center">
              📞
            </div>
            <div>
              <p className="font-medium text-violet-900">Phone Support</p>
              <p className="text-sm text-violet-700">
                <a href={supportPhoneTelHref(support.phone)} className="hover:text-violet-900">
                  {support.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
